import React, { createContext, useContext, useRef } from 'react';
import { useStore } from 'zustand';
import type { StoreApi } from 'zustand';
import { createStashScopedStore } from './createStashScopedStore';
import type { HomeBase, StashCardPeopleScope, StashScopedActions, StashScopedState } from './types';

type StashScopedStore = StoreApi<StashScopedState & StashScopedActions>;

const StashScopedStoreContext = createContext<StashScopedStore | null>(null);

interface StashScopedStoreProviderProps {
  id: string;
  currentUserStasherId: string;
  scope?: StashCardPeopleScope;
  homeBase?: HomeBase | null;
  children: React.ReactNode;
}

export function StashScopedStoreProvider({
  id,
  currentUserStasherId,
  scope,
  homeBase,
  children,
}: StashScopedStoreProviderProps): React.JSX.Element {
  const storeRef = useRef<StashScopedStore | undefined>(undefined);
  if (!storeRef.current) {
    storeRef.current = createStashScopedStore(id, currentUserStasherId, scope, homeBase);
  }
  return (
    <StashScopedStoreContext.Provider value={storeRef.current}>
      {children}
    </StashScopedStoreContext.Provider>
  );
}

export function useStashScopedStore<T>(
  selector: (s: StashScopedState & StashScopedActions) => T,
): T {
  const store = useContext(StashScopedStoreContext);
  if (!store) {
    throw new Error('useStashScopedStore must be used within StashScopedStoreProvider');
  }
  return useStore(store, selector);
}
